import { useQuery } from "@tanstack/react-query";
import { format, parseISO } from "date-fns";
import { AlertTriangle } from "lucide-react";

import { useApiHeaders } from "@/hooks/use-api-headers";
import { API_URL, errorDetail, fetchJson } from "@/lib/admin-api";

/**
 * Expected versus actual for the report pipeline (POS2-1298).
 *
 * **Every other signal in reports reports a failure; this one reports an absence.** An error run, an
 * exhausted delivery and a bounce all announce themselves. A scheduler that stopped, a webhook whose
 * secret was rotated, and a row nothing will ever claim announce nothing at all — and nothing is
 * exactly what a healthy quiet night looks like. This is the query a person would run by hand,
 * standing where they already look.
 *
 * **It renders nothing when the estate is clean**, so it is a signal rather than furniture. The one
 * thing it will not do is disappear when the *check itself* fails: an observability panel that goes
 * quiet on error is the failure it exists to catch, so a failed request says so in words.
 *
 * **Not range-scoped, and it says so.** Everything else on this screen ends yesterday (decisions
 * 11/30). An overdue schedule is a fact about now, and POS2-1254 is the record of what happens when
 * a current-state field is allowed to look like a range-scoped one — hence the stamp in the header.
 *
 * **English only** — the `/admin/*` carve-out from Phase 6.4.
 */

const BASE = `${API_URL}/api/v1/admin`;

type OverdueSchedule = {
  schedule_id: string;
  site_id: string;
  site_name: string;
  report_type: string;
  next_run_at: string | null;
  hours_overdue: number | null;
};

type StalledDelivery = {
  delivery_id: string;
  site_id: string;
  site_name: string;
  recipient_email: string;
  sent_at: string;
  hours_since_sent: number;
};

type ReportPipeline = {
  checked_at: string;
  grace_hours: number;
  stall_hours: number;
  overdue: OverdueSchedule[];
  no_cursor: OverdueSchedule[];
  stalled_deliveries: StalledDelivery[];
};

export function ReportPipelinePanel() {
  const headers = useApiHeaders();
  const pipeline = useQuery<ReportPipeline>({
    queryKey: ["admin-reports-pipeline"],
    queryFn: () => fetchJson<ReportPipeline>(`${BASE}/reports-pipeline`, headers),
  });

  if (pipeline.isError) {
    return (
      <p
        role="alert"
        data-testid="pipeline-error"
        className="mb-4 text-xs text-gray-500"
        // Not an alert box: the pipeline being unreadable is worth saying and is not itself a
        // customer problem, and dressing it as one would outrank the findings below it.
      >
        The report pipeline check could not run — {errorDetail(pipeline.error)}
      </p>
    );
  }

  const data = pipeline.data;
  if (!data) return null;

  const findings = data.overdue.length + data.no_cursor.length + data.stalled_deliveries.length;
  if (findings === 0) return null;

  return (
    <div
      role="alert"
      data-testid="report-pipeline"
      className="mb-5 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3"
    >
      <p className="flex items-center gap-2 text-sm font-semibold text-amber-900">
        <AlertTriangle className="size-4 shrink-0" />
        Report pipeline · {findings} to look at
        <span className="font-normal text-amber-700">
          as of {format(parseISO(data.checked_at), "d MMM HH:mm")}, not the selected range
        </span>
      </p>

      <Finding
        testid="pipeline-overdue"
        count={data.overdue.length}
        heading={`should have sent more than ${data.grace_hours}h ago and did not`}
      >
        {data.overdue.map((row) => (
          <li key={row.schedule_id}>
            {row.site_name} · {row.report_type} · {row.hours_overdue}h overdue
          </li>
        ))}
      </Finding>

      <Finding
        testid="pipeline-no-cursor"
        count={data.no_cursor.length}
        // Says what it means rather than naming the column: a running schedule with no next send is
        // never claimed, never logged and never seen again, which is not something "next_run_at is
        // null" conveys to the person reading this at nine in the morning.
        heading="are running with no next send, so nothing will ever claim them"
      >
        {data.no_cursor.map((row) => (
          <li key={row.schedule_id}>
            {row.site_name} · {row.report_type}
          </li>
        ))}
      </Finding>

      <Finding
        testid="pipeline-stalled"
        count={data.stalled_deliveries.length}
        heading={`have had no word from the provider in ${data.stall_hours}h — check the webhook secret`}
        noun="deliveries"
      >
        {data.stalled_deliveries.map((row) => (
          <li key={row.delivery_id}>
            {row.site_name} · {row.recipient_email} · sent{" "}
            {format(parseISO(row.sent_at), "d MMM HH:mm")}
          </li>
        ))}
      </Finding>
    </div>
  );
}

function Finding({
  testid,
  count,
  heading,
  noun = "schedules",
  children,
}: {
  testid: string;
  count: number;
  heading: string;
  noun?: string;
  children: React.ReactNode;
}) {
  if (count === 0) return null;
  return (
    <div className="mt-2 text-xs text-amber-900" data-testid={testid}>
      <p className="font-medium">
        {count} {noun} {heading}
      </p>
      <ul className="mt-1 space-y-0.5 text-amber-800">{children}</ul>
    </div>
  );
}
